import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { PurchaseOrder, Shipment } from "../../api/types";
import { S } from "../../styles";

export function SimulateInboundControls({
  po,
  shipment,
  onSimulated,
}: {
  po: PurchaseOrder;
  /**
   * The order's shipment, when one exists. Only read for its
   * `quantity_received`, which is what tells us whether the order is still
   * short and therefore whether a further receipt is worth offering.
   */
  shipment?: Shipment | null;
  onSimulated: () => void;
}) {
  const queryClient = useQueryClient();

  const ordered = po.quantity ?? null;
  const alreadyReceived = shipment?.quantity_received ?? null;
  const isShort =
    ordered != null && alreadyReceived != null && alreadyReceived < ordered;

  const [qty, setQty] = useState(String(ordered ?? 1));
  const parsedQty = Number.parseInt(qty, 10);
  const qtyValid = Number.isInteger(parsedQty) && parsedQty > 0;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
    onSimulated();
  };

  const shipMutation = useMutation({
    mutationFn: () => {
      const now = new Date().toISOString();
      return api.simulateShippingNotice({
        po_number: po.po_number,
        tracking_number: `TRK-${po.po_number}-${Date.now().toString(36).toUpperCase()}`,
        carrier: "UPS",
        shipped_at: now,
        estimated_delivery: new Date(Date.now() + 5 * 86_400_000)
          .toISOString()
          .split("T")[0],
        notes: "Simulated shipping notice (dev test)",
      });
    },
    onSuccess: invalidate,
  });

  const receiveMutation = useMutation({
    mutationFn: () =>
      api.simulateReceiptConfirmation({
        po_number: po.po_number,
        erp_po_ref: po.erp_po_ref ?? undefined,
        quantity_received: parsedQty,
        received_at: new Date().toISOString(),
        notes: `Simulated receipt confirmation — ${parsedQty} unit(s) (dev test)`,
      }),
    onSuccess: invalidate,
  });

  const canShip = po.status === "Sent";
  // A received order is offered a further receipt only while it is still short.
  // The count the endpoint stores is a running total rather than a delta, so
  // this second posting is a correction of the figure, not a second delivery —
  // the data model holds one shipment per order and cannot represent two.
  const canReceive =
    po.status === "Sent" ||
    po.status === "Acknowledged" ||
    (po.status === "Received" && isShort);

  if (!canShip && !canReceive) return null;

  const err = shipMutation.error ?? receiveMutation.error;
  const msg = shipMutation.isSuccess
    ? `Shipping notice sent → ${shipMutation.data?.status}`
    : receiveMutation.isSuccess
      ? `Receipt confirmed → ${receiveMutation.data?.status}`
      : null;

  return (
    <div
      style={{ marginTop: 14, paddingTop: 12, borderTop: "1px dashed #e0e4f0" }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase" as const,
          color: "#aaa",
          letterSpacing: 0.5,
          marginBottom: 8,
        }}
      >
        Simulate Inbound Events (Dev / Test)
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap" as const,
          alignItems: "center",
        }}
      >
        {canShip && (
          <button
            style={{ ...S.btnSm, background: "#0891b2", fontSize: 11 }}
            onClick={() => shipMutation.mutate()}
            disabled={shipMutation.isPending}
          >
            📦 Simulate Shipping Notice
          </button>
        )}
        {canReceive && (
          <>
            <label
              style={{ ...S.muted, fontSize: 11 }}
              htmlFor={`qty-received-${po.id}`}
            >
              Qty received
            </label>
            <input
              id={`qty-received-${po.id}`}
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              style={{
                ...S.input,
                width: 72,
                marginBottom: 0,
                padding: "4px 8px",
                fontSize: 12,
              }}
            />
            <button
              style={{ ...S.btnSm, background: "#2f9e44", fontSize: 11 }}
              onClick={() => receiveMutation.mutate()}
              disabled={receiveMutation.isPending || !qtyValid}
            >
              ✓ Simulate Receipt Confirmation
            </button>
          </>
        )}
      </div>
      {canReceive && ordered != null && (
        <p style={{ ...S.muted, marginTop: 6, fontSize: 11 }}>
          {isShort
            ? `${alreadyReceived} of ${ordered} recorded so far. This is the running total, ` +
              `not an addition — post ${ordered} to settle the order and close its demand.`
            : `${ordered} ordered. Anything less leaves the requirements behind this order open.`}
        </p>
      )}
      {msg && <p style={{ ...S.ok, marginTop: 6, fontSize: 12 }}>✓ {msg}</p>}
      {err && (
        <p style={{ ...S.err, marginTop: 6, fontSize: 12 }}>
          ⚠ {err.message}
          <span style={{ ...S.muted, display: "block" }}>
            Check that VITE_INTERNAL_API_TOKEN is set in your .env file.
          </span>
        </p>
      )}
    </div>
  );
}
